import { z } from "zod";

export const DEFAULT_PREVIEW_PUBLIC_PORT_MIN = 61_000;
export const DEFAULT_PREVIEW_PUBLIC_PORT_MAX = 61_049;

export const RESERVED_PREVIEW_PUBLIC_PORTS = [
  80, 443, 1025, 1433, 2375, 2376, 3000, 5173, 5174, 5175, 6379, 8025, 8080,
  8081, 8082, 8083, 9090, 18_080,
] as const;

const tcpPortSchema = z.number().int().min(1).max(65_535);

export const channelEnvironmentPreviewPortSchema = z.object({
  containerPort: tcpPortSchema,
  label: z.string().trim().min(1).max(80).optional(),
  publicPort: z
    .number()
    .int()
    .min(DEFAULT_PREVIEW_PUBLIC_PORT_MIN)
    .max(DEFAULT_PREVIEW_PUBLIC_PORT_MAX)
    .refine(
      (value) => !RESERVED_PREVIEW_PUBLIC_PORTS.some((port) => port === value),
      { message: "publicPort is reserved for an Arti service" },
    ),
});

export const channelEnvironmentPreviewPortsSchema = z
  .array(channelEnvironmentPreviewPortSchema)
  .max(DEFAULT_PREVIEW_PUBLIC_PORT_MAX - DEFAULT_PREVIEW_PUBLIC_PORT_MIN + 1)
  .superRefine((ports, ctx) => {
    const containerPorts = new Set<number>();
    const publicPorts = new Set<number>();
    for (const [index, port] of ports.entries()) {
      if (containerPorts.has(port.containerPort)) {
        ctx.addIssue({
          code: "custom",
          message: "containerPort must be unique per environment",
          path: [index, "containerPort"],
        });
      }
      containerPorts.add(port.containerPort);
      if (publicPorts.has(port.publicPort)) {
        ctx.addIssue({
          code: "custom",
          message: "publicPort must be unique per environment",
          path: [index, "publicPort"],
        });
      }
      publicPorts.add(port.publicPort);
    }
  });

export type ChannelEnvironmentPreviewPort = z.infer<
  typeof channelEnvironmentPreviewPortSchema
>;

export function parseChannelEnvironmentPreviewPorts(
  raw: string | null | undefined,
): ChannelEnvironmentPreviewPort[] {
  if (!raw) {
    return [];
  }
  return channelEnvironmentPreviewPortsSchema.parse(JSON.parse(raw) as unknown);
}

export function safeParseChannelEnvironmentPreviewPorts(
  raw: string | null | undefined,
  onError?: (error: unknown) => void,
): ChannelEnvironmentPreviewPort[] {
  try {
    return parseChannelEnvironmentPreviewPorts(raw);
  } catch (error) {
    onError?.(error);
    return [];
  }
}

export function serializeChannelEnvironmentPreviewPorts(
  ports: readonly ChannelEnvironmentPreviewPort[],
): string {
  return JSON.stringify(channelEnvironmentPreviewPortsSchema.parse(ports));
}
